<script setup lang="ts">
import { computed, defineEmits, h, onMounted, ref } from 'vue'

import { NButton, NIcon, NTooltip, type PaginationProps, useDialog, useMessage } from 'naive-ui'
import FormCommon from '../../../views/classroom-v2/components/FormCommon.vue'
import FormEditClassroom from './FormEditClassroom.vue'
import columnClassInfo from './columnClassInfo'
import { t } from '@/locales'
import { deleteClassroom, getListClassroom } from '@/api/classroom'
import { AppSvgEIcon, SvgIcon } from '@/components/common'
import { COMMON, PAGINATION } from '@/constants'
import { useClassroomStore, useUserStore } from '@/store'
import { isUserRole } from '@/constants/role'

const props = defineProps<{
  show: boolean
}>()

const emit = defineEmits(['onClose'])

const message = useMessage()
const dialog = useDialog()
const userStore = useUserStore()
const classroomStore = useClassroomStore()

const showModal = ref(props.show)

const workSpaceId = computed(() => userStore.workSpaceId)
const userRole = computed(() => userStore.role)
const loading = ref(false)
const dataSource = ref<any[]>([])
const showEditForm = ref(false)
const selectedClassroom = ref<any>(null)
const pagination = ref<PaginationProps>({
  ...PAGINATION,
  pageSize: 5,
  pageSizes: [5, 10, 30, 50],
})
const showCommon = ref<boolean>(false)

const getDataClassrooms = async () => {
  try {
    loading.value = true
    const params = {
      keyword: null,
      page: pagination?.value.page || null,
      page_size: pagination?.value.pageSize || null,
      sort: false,
      sort_by: COMMON.SORT_BY.CREATION_DATE,
      workspace_id: workSpaceId.value,
    }

    const { data } = await getListClassroom(params)
    console.log('data', data)
    if (data.success) {
      dataSource.value = data.data?.items || []
      pagination.value.itemCount = data?.data?.pagination.total || 0
      pagination.value.pageCount = data?.data?.pagination.page || 0
    }
  }
  catch (error) {
    console.log('error', error)
  }
  finally {
    loading.value = false
  }
}

const closeModal = async (reload: boolean) => {
  showModal.value = false
  if (isUserRole(userRole.value, 'ADMIN')) {
    const pagination = {
      ...PAGINATION,
      pageSize: 30,
      pageSizes: [10, 30, 50],
    }
    await classroomStore.fetchClassrooms(workSpaceId.value, '', pagination)
  }
  else {
    await classroomStore.fetchClassrooms(workSpaceId.value)
  }

  emit('onClose', reload)
}

const handleCancel = () => {
  closeModal(false)
}

const handleEdit = (classroom: any) => {
  selectedClassroom.value = classroom
  showEditForm.value = true
}

const handleAddClassroom = () => {
  showCommon.value = true
}

const onCancelDelete = () => {
  dialog.destroyAll()
}

const onDelete = async (row: any) => {
  try {
    const { data } = await deleteClassroom({ classroom_id: row.id })
    if (data && data.success) {
      message.success(data.message)
      getDataClassrooms()
    }
    else {
      message.error(data.message)
    }
  }
  catch (e) {
    message.error('Đã có lỗi xảy ra')
  }
  finally {

  }
  dialog.destroyAll()
}

const handleDelete = (row: any) => {
  const title = t('classroom.titleDelete')
  const content = t('classroom.contentDelete')
  const checkboxText = t('classroom.deleteConfirmCheckbox')

  let isChecked = false

  dialog.warning({
    title,
    showIcon: false,
    autoFocus: false,
    maskClosable: false,
    closable: true,
    style: {
      width: '500px',
      maxWidth: '90vw',
    },
    content: () =>
      h('div', { class: 'text-center' }, [
        h('div', { class: 'mb-4 leading-relaxed' }, content),
        h('div', { class: 'flex items-start gap-3 text-left mb-4' }, [
          h('input', {
            type: 'checkbox',
            class: 'mt-1 w-4 h-4 accent-purple-600',
            onChange: (e: any) => {
              isChecked = e.target.checked
              const deleteBtn = document.querySelector('.delete-confirm-btn') as HTMLElement
              if (deleteBtn) {
                deleteBtn.style.opacity = isChecked ? '1' : '0.5'
                deleteBtn.style.pointerEvents = isChecked ? 'auto' : 'none'
              }
            },
          }),
          h('span', { class: 'text-sm text-gray-700 leading-relaxed' }, checkboxText),
        ]),
      ]),
    action: () =>
      h('div', { class: 'flex gap-3 justify-center mt-6' }, [
        h(
          NButton,
          {
            size: 'medium',
            class: ' px-8 py-2 border border-purple-300 text-purple-600 bg-white hover:bg-purple-50',
            onClick: () => onCancelDelete(),
          },
          { default: () => t('common.cancel') },
        ),
        h(
          NButton,
          {
            type: 'primary',
            size: 'medium',
            class: 'delete-confirm-btn  px-8 py-2 bg-purple-600 text-white hover:bg-purple-700',
            style: 'opacity: 0.5; pointer-events: none;',
            onClick: () => {
              if (isChecked)
                onDelete(row)
            },
          },
          { default: () => t('common.delete') }, // "Xoá"
        ),
      ]),
  })
}

// Render cell for AppTable
const renderCell = (value: any, rowData: any, column: any) => {
  switch (column.key) {
    case 'index':
      return dataSource.value.indexOf(rowData) + 1
    case 'name':
      return rowData?.name
    case 'memberCount':
      return rowData?.memberCount ?? 0
    case 'actions':
      return h(
        'div',
        { class: 'flex items-center justify-center gap-2' },
        [
          h(
            NTooltip,
            { trigger: 'hover' },
            {
              trigger: () =>
                h(
                  NButton,
                  { quaternary: true, size: 'small', text: true, onClick: () => handleEdit(rowData) },
                  {
                    icon: () =>
                      h(NIcon, null, {
                        default: () => h(AppSvgEIcon, { name: 'edit-square' }),
                      }),
                  },
                ),
              default: () => t('common.edit'),
            },
          ),
          h(
            NTooltip,
            { trigger: 'hover' },
            {
              trigger: () =>
                h(
                  NButton,
                  { quaternary: true, size: 'small', text: true, onClick: () => handleDelete(rowData) },
                  {
                    icon: () =>
                      h(NIcon, null, {
                        default: () => h(SvgIcon, { icon: 'ion:trash-outline', class: 'text-red-500' }),
                      }),
                  },
                ),
              default: () => t('common.delete'),
            },
          ),
        ],
      )
    default:
      return value
  }
}

const onCloseEditForm = (reload: boolean) => {
  showEditForm.value = false
  selectedClassroom.value = null
  if (reload)
    getDataClassrooms()
}

const onClose = async (reload: boolean) => {
  showCommon.value = false
  if (reload)
    getDataClassrooms()
}

onMounted(async () => {
  await getDataClassrooms()
})
</script>

<template>
  <AppModal
    v-model:show="showModal"
    preset="card"
    transform-origin="center"
    style="width: 600px"
    class="!rounded-2xl"
    content-class="!px-4 !py-0"
    header-class="!p-4"
    footer-class="!p-4 !w-full !pt-3"
    :mask-closable="false"
    @after-leave="handleCancel"
    @esc="handleCancel"
  >
    <template #header>
      <div class="text-center text-2xl font-semibold text-gray-900 dark:text-neutral-100">
        {{ $t('settings.userManagementDropdown.classInfo') }}
      </div>
    </template>

    <div class="w-full">
      <div class="text-center text-sm text-gray-600 dark:text-neutral-400 ">
        <span v-html="$t('classroom.classListDescription')" />
      </div>

      <!-- AppTable -->
      <AppTable
        :columns="columnClassInfo"
        :data-source="dataSource"
        :pagination="false"
        :loading="loading"
        :render-cell="renderCell"
        :show-header-content="false"
        :show-header="true"
        class="mt-2"
        :show-card="false"
      />
    </div>

    <template #footer>
      <div class="w-full flex gap-3 justify-end items-center">
        <AppButton
          ghost
          type="primary"
          class=" max-w-[180px] !w-full"
          size="medium"
          @click="handleCancel"
        >
          {{ $t('common.close') }}
        </AppButton>
        <AppButton
          type="primary"
          class=" max-w-[180px] !w-full"
          size="medium"
          @click="handleAddClassroom"
        >
          {{ $t('classroom.add') }}
        </AppButton>
      </div>
    </template>
  </AppModal>

  <!-- Edit Classroom Form -->
  <FormEditClassroom
    v-if="showEditForm"
    :show="showEditForm"
    :data-source="selectedClassroom"
    @on-close="onCloseEditForm"
  />
  <FormCommon
    v-if="showCommon"
    :data-source="{}"
    :show="showCommon"
    :is-view="false"
    :is-edit="false"
    :is-create="true"
    :title="$t('classroom.create')"
    @on-close="onClose"
  />
</template>

<style scoped>
table {
  min-width: 100%;
}
</style>
